/**
 * "Agent Notes" — the memory surface, built out of ORDINARY documents
 * (owner decision **O4**).
 *
 * There is no new record type and no hidden store. A new `record_type` would
 * be invisible to `listTree` (which enumerates `folder` and `document` only),
 * i.e. memory the user could neither see nor delete — exactly the wrong
 * property for something an agent writes unsupervised. Instead the folder is a
 * real `folder` record carrying `data.mcpMemory: true` (extra `data` keys ride
 * through `record_json`'s flat spread harmlessly and `listTree` ignores them),
 * and each note is a real `document` + `docbody` inside it. Everything an
 * agent writes is visible in Apiboo's own sidebar and deletable there.
 *
 * Beside `documentsStore.js` rather than inside it: this is an MCP concept,
 * and the store is shared with the whole documents extension.
 */
import { listRecords, upsertRecord } from '../../../records/recordsClient';
import {
  recordsContext,
  listTree,
  createFolder,
  createDocument,
  readDocument,
  saveDocumentBody,
  deleteNodes,
  padKey,
  uid,
  MEMORY_FOLDER_FLAG,
} from './documentsStore';

const EXTENSION_ID = 'documents';
/**
 * The marker that makes a folder the agent-notes folder for its scope. The
 * notes directly in it are not counted against the plan's document cap
 * (documentsStore.countOwnDocuments), so they never refuse the user's own.
 */
export const MEMORY_FLAG = MEMORY_FOLDER_FLAG;

/** The folder record for `scope`, or null. Never creates. */
export async function findMemoryFolder(scope) {
  const ctx = recordsContext(scope);
  if (!ctx) return null;
  const folders = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: 'folder',
    scopeId: null,
  });
  const hit = (folders || []).find((f) => f[MEMORY_FLAG] === true);
  return hit ? { id: hit.id, name: hit.name || '', sortKey: hit.sort_key || '' } : null;
}

/**
 * The folder for `scope`, created at the tree root on first use.
 *
 * `folderName` comes from the caller (the i18n `mcp.memoryFolderName`) so this
 * module stays free of a translation dependency and the folder is named in the
 * user's own language.
 */
export async function ensureMemoryFolder(scope, folderName) {
  const existing = await findMemoryFolder(scope);
  if (existing) return existing;
  const ctx = recordsContext(scope);
  if (!ctx) throw new Error('unavailable');
  const id = uid();
  await upsertRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    record: {
      id,
      record_type: 'folder',
      parent_id: null,
      sort_key: padKey(0),
      data: { name: folderName, [MEMORY_FLAG]: true },
    },
  });
  return { id, name: folderName, sortKey: padKey(0) };
}

/** The notes in `scope`'s folder — tree rows, so no bodies are loaded. */
export async function listNotes(scope) {
  const folder = await findMemoryFolder(scope);
  if (!folder) return { folder: null, notes: [] };
  const tree = await listTree(scope);
  const notes = tree.filter((n) => n.type === 'document' && n.parentId === folder.id);
  return { folder, notes };
}

/**
 * The note document for `title` in `scope`, created if absent.
 * One note per title keeps each one well under the 32 KB per-record sync cap;
 * the default title is the date, so a session's notes land together.
 */
export async function ensureNote(scope, folderName, title) {
  const folder = await ensureMemoryFolder(scope, folderName);
  const tree = await listTree(scope);
  const existing = tree.find(
    (n) => n.type === 'document' && n.parentId === folder.id && n.name === title
  );
  if (existing) return { folder, note: existing, created: false };
  const created = await createDocument(
    { parentId: folder.id, title, sortKey: padKey(Date.now() % 1e12) },
    scope
  );
  return { folder, note: created, created: true };
}

export { readDocument, saveDocumentBody, deleteNodes, createFolder, listTree };
